<template>
  <div class="group/dropdown" :class="{ show: dropdownOpen }">
    <div
      ref="dropdownButton"
      class="flex items-center"
      :class="[
        buttonClass,
        { 'not-[.flex-col]:justify-between': chevronSize > 0 },
      ]"
      type="button"
      :aria-expanded="dropdownOpen.toString()"
      @click="toggleDropdown"
      v-hint="hint ?? { enabled: false }"
    >
      <div :class="buttonContentClass">
        <slot name="label-icon"></slot>
        <span class="truncate">
          <slot name="label"> </slot>
        </span>
      </div>
      <slot name="chevron">
        <lucide-icon
          v-if="chevronSize > 0"
          class="ms-0.5 shrink-0 transition-transform group-[.show]/dropdown:rotate-180"
          icon="ChevronDown"
          :size="chevronSize"
          :class="{ rotated: dropdownOpen }"
          :style="`flex: 0 0 ${chevronSize}`"
        />
      </slot>
    </div>
    <portal to="dropdownsContent" :disabled="!fullscreen">
      <div>
        <div
          v-if="fullscreen && dropdownOpen"
          class="group-[.show]/dropdown:animate-fade-in group-not-[.show]/dropdown:pointer-events-none z-60 fixed left-0 top-0 h-full w-full bg-black/50"
        ></div>

        <transition name="slide-fade">
          <div
            v-show="dropdownOpen"
            ref="dropdownContent"
            v-click-outside="{
              enabled: dropdownOpen,
              callback: clickOutside,
            }"
            v-intersection-observer="{
              enabled: dropdownOpen && !fullscreen,
              onLeave: intersectionObserverCallback,
            }"
            class="z-60 border-gray-200 bg-white pe-1 ps-2 pt-2 shadow-lg shadow-gray-400/20 dark:border-neutral-800 dark:bg-neutral-900 dark:shadow-none"
            :class="{
              'fixed !bottom-[calc(0.125rem+env(safe-area-inset-bottom))] !left-1 !top-auto !w-[calc(100%-var(--spacing)*2)] rounded-xl border-0 pb-4':
                fullscreen,
              'border-1 absolute w-60 rounded-lg pb-2': !fullscreen,
            }"
          >
            <div
              v-if="$slots.title"
              class="border-b-1 mb-2 flex items-center justify-between border-gray-100 pb-2 pe-1 ps-1 pt-1 dark:border-neutral-800"
            >
              <div class="grow truncate leading-4">
                <slot name="title"></slot>
              </div>
              <button
                class="hover:text-primary ms-2 h-6 w-6 cursor-pointer rounded bg-gray-100 p-1 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                @click.prevent="closeDropdown"
              >
                <lucide-icon icon="X" size="16" />
              </button>
            </div>
            <div
              class="custom-scroll-y overflow-y-auto pe-1"
              :class="{ 'max-h-80 ': fullscreen, 'max-h-40': !fullscreen }"
            >
              <slot name="content" :close-dropdown="closeDropdown"></slot>
            </div>
          </div>
        </transition>
      </div>
    </portal>
  </div>
</template>

<script>
import ClickOutside from '@/libs/directives/ClickOutside';
import IntersectionObserver from '@/libs/directives/IntersectionObserver';
import Hint from '@/libs/directives/Hint';

import {
  computePosition,
  flip,
  offset,
  autoUpdate,
  shift,
} from '@floating-ui/dom';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Dropdown',
  directives: {
    ClickOutside,
    IntersectionObserver,
    Hint,
  },
  props: {
    position: {
      type: String,
      default: 'bottom-start',
    },
    buttonClass: {
      type: String,
      default: 'w-100 btn btn-secondary',
    },
    buttonContentClass: {
      type: String,
      default: 'flex items-center',
    },
    yOffset: {
      type: Number,
      default: 5,
    },

    chevronSize: {
      type: Number,
      default: 14,
    },
    fullscreen: {
      type: [Number, Boolean],
      default: false,
    },
    hint: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      dropdownOpen: false,
      cleanup: null,
    };
  },

  mounted() {},
  beforeDestroy() {
    if (this.cleanup != null) {
      this.cleanup();
      this.cleanup = null;
    }
  },
  methods: {
    intersectionObserverCallback() {
      this.closeDropdown();
    },
    clickOutside() {
      this.closeDropdown();
    },

    closeDropdown() {
      this.dropdownOpen = false;
      this.$emit('closed');
      document.body.classList.remove('fullscreen-dropdown-open');

      if (this.cleanup != null) {
        this.cleanup();
        this.cleanup = null;
      }
    },

    updatePosition() {
      const floatingEl = this.$refs.dropdownContent;
      const referenceEl = this.$refs.dropdownButton;

      computePosition(referenceEl, floatingEl, {
        placement: this.position,
        middleware: [offset(this.yOffset), flip(), shift({ padding: 10 })],
      }).then((data) => {
        this.setDropdownPosition({
          left: data.x,
          top: data.y,
        });
      });
    },

    openDropdown() {
      this.dropdownOpen = true;
      this.$emit('opened');
      if (this.fullscreen) {
        document.body.classList.add('fullscreen-dropdown-open');
        return;
      }

      const floatingEl = this.$refs.dropdownContent;
      const referenceEl = this.$refs.dropdownButton;
      this.cleanup = autoUpdate(referenceEl, floatingEl, this.updatePosition);
    },

    setDropdownPosition({ left, top }) {
      this.$refs.dropdownContent.style.left =
        left !== null ? `${left}px` : 'auto';
      this.$refs.dropdownContent.style.right = 'auto';
      this.$refs.dropdownContent.style.top = top !== null ? `${top}px` : 'auto';
      this.$refs.dropdownContent.style.bottom = 'auto';
    },

    toggleDropdown() {
      if (this.dropdownOpen) {
        this.closeDropdown();
        return;
      }
      this.openDropdown();
    },
  },
};
</script>
<style lang="scss">
.fullscreen-dropdown-open {
  overflow: hidden;
}
</style>
